import type { ComponentTransform } from "building-types/components/componentTypes";
import type { DocBuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { InteriorMaterial } from "building-types/materials/interiorPaletteTypes";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { RoomTag } from "./roomTag";
import type { RoomType } from "./roomTypesEnum";
import type { RoomAttributes } from "./staticRoomAttributes";

export interface RoomGeometry {
  readonly outline: PolyLine2;
  readonly edgeIds: string[];
}

// Room is an immutable representation of an area in the building. A room may be a porch
// and it may not have walls around it. Room is immutable by design. If you wish to add a
// field to Room that can't be immutable, consider making a new field on Building
// that maps from the room id to your desired data type.

export interface Room extends DocBuildingEntityBase<"ROOM">, RoomTag, RoomGeometry {
  readonly type: RoomType;
  readonly name: string;
  readonly level: Level;
  readonly parentId: string;
  readonly attributes: RoomAttributes;
  readonly ceilingHeightOffset: number;
  readonly ceilingMaterial?: PaletteMaterialSlot;
  /**
   * Optional finish assembly material to be applied to this room.
   * @TODO - this should be a room level operator
   */
  readonly wallMaterial?: InteriorMaterial;
  /**
   * If this room was generated from another room (e.g., component instance),
   * this stores the original source room ID and the component instance ID.
   */
  readonly sourceRoomId?: string;
  readonly instanceId?: string;
  readonly componentTransform?: ComponentTransform;
  /**
   * A typed DocEntityHandle for type safety
   */
  readonly roomHandle: RoomHandle;
}

export const ROOF_VIRTUAL_ROOM_NAME = "Roof Room";
